import { Component, Inject, Optional } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { U2bValidators } from '@shared/validators/validators';
import { AppNotificationService } from '@core/services/app-notification.service';
import { PayableOptionId } from '@bcmApiServices/product-payable-option.api-service';
import { hours, minutes } from '@shared/constants/date';
import { TenantRelationAssignment } from '@shared/models/tenant-relation-assignment';
import { ProductSubscription } from '@shared/models/product-subscription';
import { removeErrorFromControl } from '@core/functions/remove-error-from-controls';
import { toDateTimeStringDE } from '@core/functions/to-date-string';
import { tryParseDate } from '@shared/functions/try-parse-date';
import { TolgeeService } from '@core/translation/tolgee.service';

@Component({
    selector: 'get-product-subscription-end-dialog',
    templateUrl: './get-product-subscription-end-dialog.component.html',
    standalone: false
})
export class GetProductSubscriptionEndDialogComponent {
    formGroup: UntypedFormGroup;
    PayableOptionId = PayableOptionId;
    hours = hours;
    minutes = minutes;
    isSaving: boolean;
    dateStartAt = new Date();
    dateFilter = null;
    dateStartView: 'month' | 'year' | 'multi-year' = 'month';

    subscription: TenantRelationAssignment | ProductSubscription;
    currentPayableOptionId: PayableOptionId;
    modalTitle: string;

    constructor(
        public dialogRef: MatDialogRef<GetProductSubscriptionEndDialogComponent>,
        @Optional() @Inject(MAT_DIALOG_DATA) private data: {
            subscription: TenantRelationAssignment | ProductSubscription,
            modalTitle?: string
        },
        private _formBuilder: UntypedFormBuilder,
        private _appNotificationService: AppNotificationService,
        private _tolgee: TolgeeService,
    ) {
        this.dialogRef.addPanelClass('default-dialog');
        this.subscription = data.subscription;
        this.currentPayableOptionId = data.subscription?.payableOption?.id;
        this.modalTitle = data.modalTitle || this._tolgee.translate('dialogs.productSubscription.endDialogDefaultTitle');
        this.formGroup = this._createForm();
    }

    saveAndContinue(): void {
        if (this.formGroup.invalid) {
            this.formGroup.markAllAsTouched();
            this._appNotificationService.showError(this._tolgee.translate('common.checkFieldsHighlight'));
            return;
        }

        this.isSaving = true;
        this.dialogRef.close(this.formGroup.value?.date);
    }

    private _createForm(): UntypedFormGroup {
        const form = this._formBuilder.group({
            date: [null, [U2bValidators.required(this._tolgee.translate('common.validationMsg.subscriptionEndDateRequired'))]],
        });

        const dateControl = form.get('date');

        form.valueChanges.subscribe(value => {
            if (!value) {
                return;
            }

            const {date} = value;

            if (!date) {
                return;
            }

            const start = tryParseDate(this.subscription.fromDate);
            const endIsBeforeMinEndDate = date < start;

            if (endIsBeforeMinEndDate) {
                dateControl.setErrors({
                    wrongDateRange: {
                        message: this._tolgee.translate('dialogs.productSubscription.endDateMustBeOnOrAfter', {
                            date: toDateTimeStringDE(start),
                        }),
                    }
                });
            } else {
                removeErrorFromControl('wrongDateRange', dateControl);
            }
        });

        return form;
    }
}
